/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { CommandBarButton } from '@fluentui/react/lib/Button';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { Shimmer, ShimmerElementType } from '@fluentui/react/lib/Shimmer';
import { getId } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { LuisDebouncedCollapsableSearchBox } from 'src/core/components/LuisDebouncedCollapsableSearchBox';
import { LuisText } from 'src/core/components/LuisText';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { TableCommandBarProps } from 'src/core/components/TableCommandBarProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { FileType } from 'src/core/types/FileType';
import { getCommandBarButtonStyles } from 'src/core/utils/azureRbacStyles';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { commandBarItemSelectionManager } from 'src/core/utils/commandBarItemSelectionManager';
import { createTeachingBubble, dequeueTeachingBubble } from 'src/luis/projects/events/utils/teachingBubbles';
import { QnaMakerSynonymStore } from 'src/qnaMaker/core/stores/QnaMakerSynonymStore';
import { qnaMakerTeachingBubbleMessages } from 'src/qnaMaker/core/utils/qnaMakerTeachingBubbleMessages';

export type QnaMakerSynonymCommandBarProps = TableCommandBarProps<QnaMakerSynonymStore> & {
    /**
     * A boolean flag indicating if there are unsaved changes.
     */
    isInDirtyState: boolean;

    /**
     * A boolean flag indicating if there are no synonyms.
     */
    isInEmptyState: boolean;

    /**
     * QnA pairs count text.
     */
    synonymsCountText: string;

    /**
     * Current search query.
     */
    searchQuery: string;

    /**
     * A callback function to change the search query.
     */
    onChangeSearchQuery: (query: string) => void;

    /**
     * A callback on save changes click.
     */
    onSave: () => void;

    onDelete: () => void;

    /**
     * A boolean flag indicating if there are no synonyms being edited.
     */
    isEditSynonymOn: boolean;

    /**
     * A callback to edit synonym.
     */
    onEdit: () => void;

    /**
     * A callback on import button click.
     */
    onImport: (fileFormat: Extract<FileType, 'xlsx' | 'tsv'>) => void;

    /**
     * A callback on export button click.
     */
    onExport: (fileFormat: Extract<FileType, 'xlsx' | 'tsv'>) => void;

    /**
     * A boolean flag indicating if there are no synonyms.
     */
    isAddSynonymOn: boolean;

    /**
     * A callback on add synonym button click.
     */
    onAddNewSynonym: () => void;

    /**
     * A boolean flag indicating if there is a save operation in the background
     * to disable all edit actions.
     */
    areEditActionsDisabled: boolean;

    /**
     * Optional flag to display the command bar in loading state.
     */
    isLoading?: boolean;
};

const messages = defineMessages({
    searchPlaceholder: {
        id: 'qnaMaker.qnaMakerSynonymCommandBar.searchPlaceholder',
        defaultMessage: 'Search synonyms'
    },
    addSynonyms: {
        id: 'qnaMaker.qnaMakerSynonymCommandBar.addSynonyms',
        defaultMessage: 'Add synonyms'
    },
    saveChanges: {
        id: 'qnaMaker.qnaMakerSynonymCommandBar.saveChanges',
        defaultMessage: 'Save changes'
    },
    edit: {
        id: 'qnaMaker.qnaMakerSynonymCommandBar.edit',
        defaultMessage: 'Edit'
    },
    deleteSynonymGroup: {
        id: 'qnaMaker.qnaMakerSynonymCommandBar.deleteSynonymGroup',
        defaultMessage: 'Delete synonym group'
    },
    deleteFollowUpPrompts: {
        id: 'qnaMaker.qnaMakerSynonymCommandBar.deleteFollowUpPrompts',
        defaultMessage: 'Delete follow up prompts'
    },
    import: {
        id: 'qnaMaker.qnaMakerSynonymCommandBar.import',
        defaultMessage: 'Import'
    },
    export: {
        id: 'qnaMaker.qnaMakerSynonymCommandBar.export',
        defaultMessage: 'Export'
    },
    exportSynonymsExcel: {
        id: 'qnaMaker.qnaMakerSynonymCommandBar.exportSynonymsExcel',
        defaultMessage: 'Export as Excel'
    },
    exportSynonymsTsv: {
        id: 'qnaMaker.qnaMakerSynonymCommandBar.exportSynonymsTsv',
        defaultMessage: 'Export as TSV'
    },
    importSynonymsExcel: {
        id: 'qnaMaker.qnaMakerSynonymCommandBar.importSynonymsExcel',
        defaultMessage: 'Import as Excel'
    },
    importSynonymsTsv: {
        id: 'qnaMaker.qnaMakerSynonymCommandBar.importSynonymsTsv',
        defaultMessage: 'Import as TSV'
    }
});

/**
 * Command bar rendering all available models and filter options
 */
export const QnaMakerSynonymCommandBar = observer((props: QnaMakerSynonymCommandBarProps) => {
    const {
        onSave,
        onEdit,
        onDelete,
        onExport,
        onImport,
        synonymsCountText,
        searchQuery,
        onAddNewSynonym,
        isInDirtyState,
        isAddSynonymOn,
        isInEmptyState,
        isEditSynonymOn,
        isLoading = false,
        onChangeSearchQuery,
        areEditActionsDisabled,
        selectedItems
    } = props;

    const { localize } = useLocalization();
    const { uiStore, rootStore } = useGlobalState();

    const addButtonIdRef = React.useRef<string>(getId());
    const saveChangesButtonIdRef = React.useRef<string>(getId());
    const editButtonIdRef = React.useRef<string>(getId());
    const importButtonIdRef = React.useRef<string>(getId());
    const exportButtonIdRef = React.useRef<string>(getId());
    const deleteButtonIdRef = React.useRef<string>(getId());

    const [isAddButtonDisabled, setIsAddButtonDisabled] = React.useState(true);
    const [isEditButtonDisabled, setIsEditButtonDisabled] = React.useState(true);
    const [isSaveButtonDisabled, setIsSaveButtonDisabled] = React.useState(true);
    const [isDeleteButtonDisabled, setIsDeleteButtonDisabled] = React.useState(true);

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite } = mapUserRoleToUserActions(userRole);

    React.useEffect(
        () => () => {
            dequeueTeachingBubble('SynonymEdit');
            dequeueTeachingBubble('SynonymDelete');
            dequeueTeachingBubble('QnaAddSynonyms');
            dequeueTeachingBubble('SynonymSaveChanges');
        },
        []
    );

    React.useEffect(() => {
        if (!isAddButtonDisabled && !uiStore.teachingBubbles.QnaAddSynonyms) {
            createTeachingBubble(
                'QnaAddSynonyms',
                localize(qnaMakerTeachingBubbleMessages.addSynonymBody),
                localize(qnaMakerTeachingBubbleMessages.addSynonymTitle),
                `#${addButtonIdRef.current}`,
                true,
                true
            );

            return;
        }

        if (
            isInDirtyState &&
            !isSaveButtonDisabled &&
            !uiStore.teachingBubbles.QnaSaveChanges &&
            !uiStore.teachingBubbles.SynonymSaveChanges
        ) {
            createTeachingBubble(
                'SynonymSaveChanges',
                localize(qnaMakerTeachingBubbleMessages.synonymSaveChangesBody),
                localize(qnaMakerTeachingBubbleMessages.saveChangesTitle),
                `#${saveChangesButtonIdRef.current}`,
                true
            );

            return;
        }

        if (!isEditButtonDisabled && !uiStore.teachingBubbles.QnaEdit && !uiStore.teachingBubbles.SynonymEdit) {
            createTeachingBubble(
                'SynonymEdit',
                localize(qnaMakerTeachingBubbleMessages.synonymEditBody),
                localize(qnaMakerTeachingBubbleMessages.synonymEditTitle),
                `#${editButtonIdRef.current}`,
                true,
                true
            );

            return;
        }

        if (
            !isDeleteButtonDisabled &&
            uiStore.teachingBubbles.SynonymEdit &&
            !uiStore.teachingBubbles.QnaDelete &&
            !uiStore.teachingBubbles.SynonymDelete &&
            !uiStore.skippedTeachingBubbles.SynonymEdit
        ) {
            createTeachingBubble(
                'SynonymDelete',
                localize(qnaMakerTeachingBubbleMessages.synonymDeleteBody),
                localize(qnaMakerTeachingBubbleMessages.synonymDeleteTitle),
                `#${deleteButtonIdRef.current}`,
                true
            );
        }
    }, [
        isInDirtyState,
        isAddButtonDisabled,
        isSaveButtonDisabled,
        isEditButtonDisabled,
        isDeleteButtonDisabled,
        Object.values(uiStore.teachingBubbles)
    ]);

    const items: ICommandBarItemProps[] = [
        {
            key: 'add',
            onRender: (item: ICommandBarItemProps) => {
                const _disabled = item.disabled || isAddSynonymOn || areEditActionsDisabled;
                setIsAddButtonDisabled(_disabled);

                return (
                    <RBACTooltip
                        hidden={canWrite || _disabled}
                        displayedUserRole={displayedUserRole}
                        data-automation-id="addSynonymsButtonLanguageToolTip"
                        componentSurroundedByToolTip={
                            <CommandBarButton
                                role="menuitem"
                                onClick={onAddNewSynonym}
                                id={addButtonIdRef.current}
                                iconProps={{ iconName: 'Add' }}
                                disabled={!canWrite || _disabled}
                                name={localize(messages.addSynonyms)}
                                text={localize(messages.addSynonyms)}
                                data-automation-id="addSynonymsButton"
                                ariaLabel={localize(messages.addSynonyms)}
                                styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            />
                        }
                    />
                );
            }
        },
        {
            key: 'saveChanges',
            onRender: (item: ICommandBarItemProps) => {
                const _disabled = item.disabled || !isInDirtyState || isEditSynonymOn || isAddSynonymOn || areEditActionsDisabled;
                setIsSaveButtonDisabled(!canWrite || _disabled);

                return (
                    <RBACTooltip
                        hidden={canWrite || _disabled}
                        displayedUserRole={displayedUserRole}
                        data-automation-id="saveChangesButtonLanguageToolTip"
                        componentSurroundedByToolTip={
                            <CommandBarButton
                                role="menuitem"
                                onClick={onSave}
                                iconProps={{ iconName: 'Save' }}
                                disabled={!canWrite || _disabled}
                                id={saveChangesButtonIdRef.current}
                                name={localize(messages.saveChanges)}
                                text={localize(messages.saveChanges)}
                                data-automation-id="saveChangesButton"
                                ariaLabel={localize(messages.saveChanges)}
                                styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            />
                        }
                    />
                );
            }
        },
        {
            key: 'edit',
            onRender: (item: ICommandBarItemProps) => {
                const _disabled = item.disabled || areEditActionsDisabled;
                setIsEditButtonDisabled(_disabled);

                return (
                    <RBACTooltip
                        hidden={canWrite || _disabled}
                        displayedUserRole={displayedUserRole}
                        data-automation-id="editSynonymsButtonLanguageToolTip"
                        componentSurroundedByToolTip={
                            <CommandBarButton
                                role="menuitem"
                                onClick={onEdit}
                                id={editButtonIdRef.current}
                                name={localize(messages.edit)}
                                text={localize(messages.edit)}
                                iconProps={{ iconName: 'Edit' }}
                                disabled={!canWrite || _disabled}
                                ariaLabel={localize(messages.edit)}
                                data-automation-id="editSynonymsButton"
                                styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            />
                        }
                    />
                );
            }
        },
        {
            key: 'import',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    displayedUserRole={displayedUserRole}
                    hidden={canWrite || item.disabled || areEditActionsDisabled}
                    data-automation-id="importSynonymsButtonLanguageToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            role={'menuitem'}
                            id={importButtonIdRef.current}
                            name={localize(messages.import)}
                            text={localize(messages.import)}
                            iconProps={{ iconName: 'Upload' }}
                            ariaLabel={localize(messages.import)}
                            data-automation-id="importSynonymsButton"
                            menuProps={{
                                items: [
                                    {
                                        key: 'importExcel',
                                        role: 'menuitem',
                                        name: localize(messages.importSynonymsExcel),
                                        onClick: () => onImport('xlsx')
                                    },
                                    {
                                        key: 'importTsv',
                                        role: 'menuitem',
                                        name: localize(messages.importSynonymsTsv),
                                        onClick: () => onImport('tsv')
                                    }
                                ],
                                ariaLabel: localize(messages.import)
                            }}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            disabled={!canWrite || item.disabled || areEditActionsDisabled}
                        />
                    }
                />
            )
        },
        {
            key: 'export',
            id: exportButtonIdRef.current,
            text: localize(messages.export),
            iconProps: { iconName: 'Download' },
            ariaLabel: localize(messages.export),
            disabled: isInEmptyState,
            role: 'menuitem',
            subMenuProps: {
                ariaLabel: localize(messages.export),
                items: [
                    { key: 'importExcel', role: 'menuitem', name: localize(messages.exportSynonymsExcel), onClick: () => onExport('xlsx') },
                    { key: 'importTsv', role: 'menuitem', name: localize(messages.exportSynonymsTsv), onClick: () => onExport('tsv') }
                ]
            }
        },
        {
            key: 'delete',
            onRender: (item: ICommandBarItemProps) => {
                const _disabled = item.disabled || areEditActionsDisabled;
                setIsDeleteButtonDisabled(_disabled);

                return (
                    <RBACTooltip
                        hidden={canWrite || _disabled}
                        displayedUserRole={displayedUserRole}
                        data-automation-id="deleteSynonymsButtonLanguageToolTip"
                        componentSurroundedByToolTip={
                            <CommandBarButton
                                role="menuitem"
                                onClick={onDelete}
                                id={deleteButtonIdRef.current}
                                disabled={!canWrite || _disabled}
                                iconProps={{ iconName: 'Delete' }}
                                data-automation-id="deleteSynonymsButton"
                                name={localize(messages.deleteSynonymGroup)}
                                text={localize(messages.deleteSynonymGroup)}
                                ariaLabel={localize(messages.deleteSynonymGroup)}
                                styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            />
                        }
                    />
                );
            }
        }
    ];

    const farItems = [
        {
            key: 'itemsCount',
            onRender: () =>
                isLoading ? (
                    <Shimmer
                        shimmerElements={[{ type: ShimmerElementType.line, width: '70px', height: 15 }]}
                        styles={{ root: { marginTop: '15px', marginRight: '10px' } }}
                    />
                ) : (
                    <LuisText
                        data-automation-id="synonymsCountText"
                        styles={{ root: { display: 'flex', alignItems: 'center', marginRight: '5px' } }}>
                        {synonymsCountText}
                    </LuisText>
                )
        },
        {
            key: 'search',
            onRender: () => (
                <LuisDebouncedCollapsableSearchBox
                    ariaLabel={localize(messages.searchPlaceholder)}
                    size={36}
                    value={searchQuery}
                    placeholder={localize(messages.searchPlaceholder)}
                    onChange={(_, newValue?: string) => onChangeSearchQuery(newValue)}
                />
            )
        }
    ];

    const updatedItems: ICommandBarItemProps[] = commandBarItemSelectionManager(
        items,
        ['add', 'saveChanges', 'import', 'export'],
        ['saveChanges', 'edit', 'delete'],
        ['saveChanges', 'delete'],
        selectedItems.length
    );

    return <LuisCommandBar items={updatedItems} farItems={farItems} />;
});
